/**
 * PapergenStudio — Mode 1 Composition (three-pane Rubric Studio pattern).
 *
 * Left:   Sources (Wiki grounding filter)
 * Center: Paper tree (numbering + marks + drag reorder)
 * Right:  Context panel (node detail editor, version history, generation)
 *
 * D7 — Follows Rubric Studio UX, MUI 7 only, no collaborative editor.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Button, IconButton, TextField,
  Divider, Chip, FormControl, InputLabel, Select, MenuItem,
  ToggleButton, ToggleButtonGroup, LinearProgress, Alert,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox, Autocomplete, Tab, Tabs,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  AutoFixHigh as AutoFixIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  getWorkspaceTree, listPaperNodes,
  createPaperNode, updatePaperNode, deletePaperNode,
  groundWiki, generateNodes, regenerateNode, regenerateSubtree,
  listNodeVersions, restoreNodeVersion, reorderPaperNode,
  renderDocx, renderLatex, renderLatexPdf,
} from '../../api/papergen';
import { TreeNode, PaperNode, GroundingResult, MarkGrid } from '../../types/papergen';

// ──────────────────────────────────────────────────
// Hooks
// ──────────────────────────────────────────────────

const API = '/api/papergen';

function useApi(token: string | null) {
  const headers = token ? { Authorization: `Token ${token}` } : {};

  const get = useCallback(async (url: string) => {
    const axios = (await import('axios')).default;
    return axios.get(url, { headers });
  }, [token]);

  return { get, headers };
}

// ──────────────────────────────────────────────────
// WorkspacePicker
// ──────────────────────────────────────────────────

interface WorkspaceSummary {
  id: number; title: string; status: string; grade: number; subject: string;
}

const WorkspacePicker: React.FC<{
  token: string | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onCreate: () => void;
}> = ({ token, selectedId, onSelect, onCreate }) => {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API}/workspaces/`, { headers: { Authorization: `Token ${token}` } })
      .then(r => r.json())
      .then((data: any) => {
        const arr = Array.isArray(data) ? data : data.results || [];
        setWorkspaces(arr);
        // Auto-select first if none selected
        if (arr.length > 0 && !selectedId) onSelect(arr[0].id);
      })
      .catch(e => console.error('fetch workspaces:', e))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
      <FormControl sx={{ minWidth: 250 }}>
        <InputLabel>Workspace</InputLabel>
        <Select
          value={selectedId || ''}
          label="Workspace"
          disabled={loading}
          onChange={e => onSelect(Number(e.target.value))}
        >
          {workspaces.map(w => (
            <MenuItem key={w.id} value={w.id}>
              {w.title} ({w.grade}{w.subject}) — {w.status}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button variant="outlined" startIcon={<AddIcon />} onClick={onCreate}>
        New
      </Button>
    </Box>
  );
};

// ──────────────────────────────────────────────────
// Sources pane (Left)
// ──────────────────────────────────────────────────

interface GroundingPage {
  subtopic_code: string;
  title: string;
  grade: number;
  modalities: string[];
  status: string;
}

const SourcesPane: React.FC<{
  token: string | null;
  onGrounded: (result: GroundingResult) => void;
}> = ({ token, onGrounded }) => {
  const [grade, setGrade] = useState<number>(11);
  const [topic, setTopic] = useState<string>('');
  const [vettedOnly, setVettedOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GroundingResult | null>(null);
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set());

  const handleGround = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params: any = { grade };
      if (topic) params.topic_num = topic;
      if (vettedOnly) params.status = 'vetted';
      const resp = await groundWiki(params, token);
      setResult(resp.data);
      onGrounded(resp.data);
    } catch (e: any) {
      console.error('grounding:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Stack spacing={1.5} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary">Curriculum Wiki Sources</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="Grade" type="number" size="small"
            value={grade} onChange={e => setGrade(Number(e.target.value))}
            sx={{ width: 80 }}
          />
          <TextField
            label="Topic" size="small"
            value={topic} onChange={e => setTopic(e.target.value)}
            placeholder="e.g. 1.1"
          />
        </Box>
        <FormControlLabel
          control={<Checkbox size="small" checked={vettedOnly} onChange={e => setVettedOnly(e.target.checked)} />}
          label={<Typography variant="body2">Vetted only</Typography>}
        />
        <Button
          variant="contained" size="small" onClick={handleGround} disabled={loading}
          startIcon={loading ? <LinearProgress sx={{ width: 16 }} /> : <AutoFixIcon />}
        >
          {loading ? 'Filtering...' : 'Filter Pages'}
        </Button>
      </Stack>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {result && (
          <>
            <Typography variant="caption" color="text.secondary">
              {result.total_count} pages, {result.gaps?.length || 0} gaps
            </Typography>
            {result.pages?.map((p: GroundingPage) => (
              <Box key={p.subtopic_code} sx={{ mb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', display: 'block', fontWeight: 'bold' }}
                >
                  {p.subtopic_code}: {p.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip label={`G${p.grade}`} size="small" variant="outlined" />
                  <Chip label={p.status} size="small" color={p.status === 'vetted' ? 'success' : 'warning'} />
                  {p.modalities?.map(m => <Chip key={m} label={m} size="small" />)}
                </Box>
              </Box>
            ))}

            {/* A7: Gap report */}
            {result.gaps && result.gaps.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" fontWeight="bold" color="warning.main">
                  Coverage Gaps ({result.gaps.length})
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: 'warning.50', maxHeight: 120, overflow: 'auto' }}>
                  {result.gaps.map((gap, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      • {gap}
                    </Typography>
                  ))}
                </Paper>
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

// ──────────────────────────────────────────────────
// Tree pane (Center) — recursive tree with numbering & drag-reorder
// ──────────────────────────────────────────────────

const INDENT = 24;

type DropPosition = 'above' | 'below' | 'inside';

/**
 * Flatten tree into a flat list of {node, depth, parentId} tuples
 * for easier drop-target computation.
 */
function flattenTree(
  nodes: TreeNode[],
  depth: number = 0,
  parentId: number | null = null,
): { node: TreeNode; depth: number; parentId: number | null }[] {
  const result: { node: TreeNode; depth: number; parentId: number | null }[] = [];
  for (const node of nodes) {
    result.push({ node, depth, parentId });
    if (node.children && node.children.length > 0) {
      result.push(...flattenTree(node.children, depth + 1, node.id));
    }
  }
  return result;
}

const TreeItem: React.FC<{
  node: TreeNode;
  depth: number;
  parentId: number | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onRegenerate: (id: number) => void;
  onReorder: (nodeId: number, params: { new_ordinal: number; new_parent_id?: number | null }) => void;
  allNodesMap: Map<number, { depth: number; parentId: number | null }>;
  onDragStartId: (id: number) => void;
  dropTarget: { nodeId: number; position: DropPosition } | null;
  draggedId: number | null;
}> = ({ node, depth, parentId, selectedId, onSelect, onDelete, onRegenerate,
        onReorder, allNodesMap, onDragStartId, dropTarget, draggedId }) => {
  const isSelected = selectedId === node.id;
  const isDragging = draggedId === node.id;

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', String(node.id));
    e.dataTransfer.effectAllowed = 'move';
    onDragStartId(node.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const PREFIX_HEIGHT = 24;

  const handleDragOverWithPosition = (e: React.DragEvent, position: DropPosition) => {
    // Don't allow dropping onto self or direct children
    if (draggedId === node.id) return;
    e.stopPropagation();
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, position: DropPosition) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedId || draggedId === node.id) return;

    const draggedMeta = allNodesMap.get(draggedId);
    if (!draggedMeta) return;

    let newOrdinal: number;
    let newParentId: number | null;

    if (position === 'inside') {
      // Make dragged node a child of this node
      newParentId = node.id;
      newOrdinal = (node.children || []).length; // append at end
    } else {
      // Insert as sibling above/below this node
      newParentId = parentId;
      // Compute ordinal from this node's position among siblings
      newOrdinal = position === 'above' ? node.ordinal : node.ordinal + 1;
    }

    onReorder(draggedId, { new_ordinal: newOrdinal, new_parent_id: newParentId });
  };

  // Visual indicators for drop zones
  const aboveHighlight = dropTarget?.nodeId === node.id && dropTarget?.position === 'above';
  const belowHighlight = dropTarget?.nodeId === node.id && dropTarget?.position === 'below';
  const insideHighlight = dropTarget?.nodeId === node.id && dropTarget?.position === 'inside';

  return (
    <>
      {/* Drop zone: ABOVE this node */}
      <Box
        onDragOver={e => handleDragOverWithPosition(e, 'above')}
        onDrop={e => handleDrop(e, 'above')}
        sx={{
          height: 4, ml: depth * INDENT + 1, mr: 1,
          bgcolor: aboveHighlight ? 'primary.main' : 'transparent',
          borderRadius: 1, transition: 'background-color 0.15s',
        }}
      />

      {/* The node itself */}
      <Box
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={e => handleDrop(e, 'inside')}
        sx={{
          display: 'flex', alignItems: 'center', py: 0.5, pl: depth * INDENT + 1,
          cursor: 'grab', borderRadius: 1,
          opacity: isDragging ? 0.4 : 1,
          bgcolor: insideHighlight ? 'primary.light' :
                   isSelected ? 'primary.light' : 'transparent',
          border: insideHighlight ? '2px dashed' : '2px solid',
          borderColor: insideHighlight ? 'primary.main' : 'transparent',
          '&:hover': { bgcolor: isSelected ? 'primary.light' : 'grey.100' },
          transition: 'opacity 0.15s, background-color 0.15s',
        }}
        onClick={() => onSelect(node.id)}
      >
        {/* Drag handle */}
        <Box
          sx={{
            mr: 0.5, display: 'flex', alignItems: 'center', cursor: 'grab',
            color: 'text.disabled', '&:hover': { color: 'text.secondary' },
          }}
          onClick={e => e.stopPropagation()}
        >
          <DragIcon fontSize="small" />
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 40, color: 'primary.main' }}>
          {node.number}
        </Typography>
        <Typography variant="body2" sx={{ flex: 1, ml: 1 }} noWrap>
          {node.heading || '(untitled)'}
        </Typography>
        {node.marks != null && (
          <Chip label={`${node.marks}m`} size="small" color="primary" variant="outlined" sx={{ mr: 0.5 }} />
        )}
        <Chip
          label={node.content_origin}
          size="small"
          color={node.content_origin === 'generated' ? 'info' : node.content_origin === 'edited' ? 'warning' : 'default'}
          variant="outlined"
          sx={{ mr: 0.5, fontSize: '0.65rem' }}
        />
        <Tooltip title="Regenerate">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onRegenerate(node.id); }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(node.id); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Children (with inside drop) */}
      {node.children?.map(child => (
        <TreeItem
          key={child.id}
          node={child}
          depth={depth + 1}
          parentId={node.id}
          selectedId={selectedId}
          onSelect={onSelect}
          onDelete={onDelete}
          onRegenerate={onRegenerate}
          onReorder={onReorder}
          allNodesMap={allNodesMap}
          onDragStartId={onDragStartId}
          dropTarget={dropTarget}
          draggedId={draggedId}
        />
      ))}

      {/* Drop zone: BELOW last child (or below this node if no children) */}
      {(!node.children || node.children.length === 0) && (
        <Box
          onDragOver={e => handleDragOverWithPosition(e, 'below')}
          onDrop={e => handleDrop(e, 'below')}
          sx={{
            height: 4, ml: depth * INDENT + 1, mr: 1,
            bgcolor: belowHighlight ? 'primary.main' : 'transparent',
            borderRadius: 1, transition: 'background-color 0.15s',
          }}
        />
      )}
    </>
  );
};

const TreePane: React.FC<{
  tree: TreeNode[];
  markGrid: MarkGrid | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onRegenerate: (id: number) => void;
  onAddChild: (parentId: number | null) => void;
  onGenerate: () => void;
  onReorder: (nodeId: number, params: { new_ordinal: number; new_parent_id?: number | null }) => void;
  generating: boolean;
}> = ({ tree, markGrid, selectedId, onSelect, onDelete, onRegenerate,
        onAddChild, onGenerate, onReorder, generating }) => {
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{ nodeId: number; position: DropPosition } | null>(null);

  // Build a flat map of all nodes for ordinal/parent lookup
  const flatNodes = flattenTree(tree);
  const allNodesMap = new Map(flatNodes.map(f => [f.node.id, { depth: f.depth, parentId: f.parentId }]));
  const totalMarks = flatNodes.reduce((s, f) => s + (f.node.total_marks || 0), 0);

  const handleDragStartId = useCallback((id: number) => {
    setDraggedId(id);
  }, []);

  const handleDragOverRoot = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropRoot = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedId) return;
    // Drop at root level — append as last root node
    const rootCount = tree.length;
    onReorder(draggedId, { new_ordinal: rootCount, new_parent_id: null });
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDropTarget(null);
  };

  // Compute drop target from drag-over position (simplified: track last hovered node)
  useEffect(() => {
    const handler = (e: DragEvent) => {
      if (!draggedId) { setDropTarget(null); return; }
      // The drop target is set by individual TreeItems via hover tracking
    };
    document.addEventListener('dragover', handler);
    return () => document.removeEventListener('dragover', handler);
  }, [draggedId]);

  // Compute taxonomy distribution
  const taxonomyCounts = { L1: 0, L2: 0, L3: 0, L1_marks: 0, L2_marks: 0, L3_marks: 0 };
  const walkTax = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      const lvl = n.taxonomy_level || '';
      if (lvl === 'L1') { taxonomyCounts.L1++; taxonomyCounts.L1_marks += n.marks || 0; }
      else if (lvl === 'L2') { taxonomyCounts.L2++; taxonomyCounts.L2_marks += n.marks || 0; }
      else if (lvl === 'L3') { taxonomyCounts.L3++; taxonomyCounts.L3_marks += n.marks || 0; }
      if (n.children) walkTax(n.children);
    }
  };
  walkTax(tree);

  return (
    <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Stack direction="row" spacing={1} sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {flatNodes.length} nodes · {totalMarks}m total
          {draggedId && ' · Dragging...'}
        </Typography>
        <Button variant="outlined" size="small" startIcon={<AutoFixIcon />} onClick={onGenerate} disabled={generating}>
          Generate
        </Button>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => onAddChild(null)}>
          Add
        </Button>
      </Stack>

      {/* A6: Taxonomy bar */}
      {(taxonomyCounts.L1 + taxonomyCounts.L2 + taxonomyCounts.L3 > 0) && (
        <Box sx={{ px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
          {taxonomyCounts.L1 > 0 && <Chip size="small" label={`L1: ${taxonomyCounts.L1}q/${taxonomyCounts.L1_marks}m`} color="success" variant="outlined" />}
          {taxonomyCounts.L2 > 0 && <Chip size="small" label={`L2: ${taxonomyCounts.L2}q/${taxonomyCounts.L2_marks}m`} color="warning" variant="outlined" />}
          {taxonomyCounts.L3 > 0 && <Chip size="small" label={`L3: ${taxonomyCounts.L3}q/${taxonomyCounts.L3_marks}m`} color="error" variant="outlined" />}
        </Box>
      )}

      <Box
        sx={{ flex: 1, overflow: 'auto', p: 1 }}
        onDragOver={handleDragOverRoot}
        onDrop={handleDropRoot}
        onDragEnd={handleDragEnd}
      >
        {tree.map(node => (
          <TreeItem
            key={node.id} node={node} depth={0} parentId={null}
            selectedId={selectedId} onSelect={onSelect}
            onDelete={onDelete} onRegenerate={onRegenerate}
            onReorder={onReorder}
            allNodesMap={allNodesMap}
            onDragStartId={handleDragStartId}
            dropTarget={dropTarget}
            draggedId={draggedId}
          />
        ))}
        {/* Drop zone at bottom of root */}
        <Box
          sx={{
            height: 8, borderRadius: 1,
            bgcolor: (draggedId && !dropTarget) ? 'primary.light' : 'transparent',
            transition: 'background-color 0.15s',
          }}
        />
        {tree.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No tree yet. Generate content or add nodes manually.
          </Typography>
        )}
      </Box>

      {markGrid && (
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">Mark Grid</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {markGrid.columns?.map((c: any, i: number) => (
              <Chip key={i} label={`${c.label}: ${c.marks}m`} size="small" variant="outlined" />
            ))}
            <Chip label={`Total: ${markGrid.total}m`} size="small" color="primary" />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ──────────────────────────────────────────────────
// Context panel (Right) — node editor with BlockEditor
// ──────────────────────────────────────────────────

interface SelectedNodeDetail {
  id: number;
  heading: string;
  marks: number | null;
  taxonomy_level: string;
  source_subtopic_code: string;
  content_origin: string;
  stem: import('../../types/papergen').StemContent | null;
  memo: import('../../types/papergen').StemContent | null;
  preamble: import('../../types/papergen').StemContent | null;
  answer_region: import('../../types/papergen').AnswerRegion | null;
}


const ContextPanel: React.FC<{
  token: string | null;
  workspaceId: number;
  selectedId: number | null;
  onRefresh: () => void;
}> = ({ token, workspaceId, selectedId, onRefresh }) => {
  const [node, setNode] = useState<SelectedNodeDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [versions, setVersions] = useState<any[]>([]);
  const [versionsLoaded, setVersionsLoaded] = useState(false);

  // Editor state
  const [editHeading, setEditHeading] = useState('');
  const [editMarks, setEditMarks] = useState<number | null>(null);
  const [editTaxonomy, setEditTaxonomy] = useState('');
  const [editStem, setEditStem] = useState<import('../../types/papergen').StemContent | null>(null);
  const [editMemo, setEditMemo] = useState<import('../../types/papergen').StemContent | null>(null);
  const [editAnswerRegion, setEditAnswerRegion] = useState<import('../../types/papergen').AnswerRegion | null>(null);
  const [BlockEditor, setBlockEditor] = useState<any>(null);

  // Lazy-load BlockEditor to avoid circular bundle issues
  const [BlockEditorComp, setBlockEditorComp] = useState<any>(null);
  useEffect(() => {
    import('../papergen/BlockEditor').then(m => setBlockEditorComp(() => m.default));
  }, []);

  useEffect(() => {
    if (!selectedId || !token) { setNode(null); return; }
    setNode(null); setVersions([]); setVersionsLoaded(false);
    loadNode(selectedId);
  }, [selectedId, token]);

  useEffect(() => {
    import('../papergen/BlockEditor').then(m => setBlockEditor(() => m.default));
  }, []);

  const loadNode = async (id: number) => {
    if (!token) return;
    setLoading(true);
    try {
      const axios = (await import('axios')).default;
      const resp = await axios.get(`${API}/nodes/${id}/`, {
        headers: { Authorization: `Token ${token}` },
      });
      const d = resp.data;
      const detail: SelectedNodeDetail = {
        id: d.id,
        heading: d.heading || '',
        marks: d.marks,
        taxonomy_level: d.taxonomy_level || '',
        source_subtopic_code: d.source_subtopic_code || '',
        content_origin: d.content_origin || 'manual',
        stem: d.stem || null,
        memo: d.memo || null,
        preamble: d.preamble || null,
        answer_region: d.answer_region || null,
      };
      setNode(detail);
      setEditHeading(detail.heading);
      setEditMarks(detail.marks);
      setEditTaxonomy(detail.taxonomy_level);
      setEditStem(detail.stem);
      setEditMemo(detail.memo);
      setEditAnswerRegion(detail.answer_region);
    } catch (e) {
      console.error('load node:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    if (!selectedId || !token) return;
    try {
      const resp = await listNodeVersions(selectedId, token);
      setVersions(resp.data);
      setVersionsLoaded(true);
    } catch (e) { console.error('load versions:', e); }
  };

  const handleSave = async () => {
    if (!selectedId || !token) return;
    // Validate stem has blocks if non-null
    if (editStem && (!editStem.blocks || !Array.isArray(editStem.blocks))) {
      alert('Invalid stem — must have a "blocks" array');
      return;
    }
    try {
      await updatePaperNode(selectedId, {
        heading: editHeading,
        marks: editMarks,
        taxonomy_level: editTaxonomy,
        stem: editStem,
        memo: editMemo,
        answer_region: editAnswerRegion,
      }, token);
      onRefresh();
    } catch (e: any) {
      console.error('save node:', e);
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDelete = async () => {
    if (!selectedId || !token) return;
    if (!window.confirm('Delete this node and all children?')) return;
    await deletePaperNode(selectedId, token);
    onRefresh();
  };

  const handleRegenerate = async () => {
    if (!selectedId || !token) return;
    await regenerateNode(selectedId, token);
    onRefresh();
    loadNode(selectedId);
  };

  const handleRestore = async (versionId: number) => {
    if (!selectedId || !token) return;
    await restoreNodeVersion(selectedId, versionId, token);
    onRefresh();
    loadNode(selectedId);
  };

  if (!selectedId) {
    return (
      <Paper variant="outlined" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">Select a node to edit</Typography>
      </Paper>
    );
  }

  if (loading) return <LinearProgress />;
  if (!node) return <Typography variant="body2" color="text.secondary">Error loading node</Typography>;

  return (
    <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Content" />
        <Tab label="Answer" />
        <Tab label="Memo" />
        <Tab label="History" />
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {tab === 0 && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">
              Node {node.id} — <Chip label={node.content_origin} size="small"
                color={node.content_origin === 'generated' ? 'info' : node.content_origin === 'edited' ? 'warning' : 'default'} />
            </Typography>

            <TextField label="Heading" size="small" fullWidth value={editHeading}
              onChange={e => setEditHeading(e.target.value)} />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Marks" type="number" size="small" sx={{ width: 100 }}
                value={editMarks ?? ''} onChange={e => setEditMarks(e.target.value ? Number(e.target.value) : null)} />
              <TextField label="Taxonomy" size="small" sx={{ width: 100 }}
                value={editTaxonomy} onChange={e => setEditTaxonomy(e.target.value)} placeholder="L1/L2/L3" />
              <TextField label="Source Subtopic" size="small"
                value={node.source_subtopic_code} disabled />
            </Box>

            {/* BlockEditor for stem */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                Stem Content
              </Typography>
              {BlockEditorComp ? (
                <BlockEditorComp content={editStem} onChange={setEditStem} />
              ) : (
                <TextField
                  multiline minRows={4} fullWidth size="small"
                  value={JSON.stringify(editStem, null, 2)}
                  disabled
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                />
              )}
            </Box>

            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" onClick={handleSave} startIcon={<SaveIcon />}>Save</Button>
              <Button variant="outlined" size="small" onClick={handleRegenerate} startIcon={<RefreshIcon />}>Regenerate</Button>
              <Button variant="outlined" color="error" size="small" onClick={handleDelete} startIcon={<DeleteIcon />}>Delete</Button>
            </Stack>
          </Stack>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Answer Region</Typography>
            <Stack spacing={2}>
              <FormControl size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={editAnswerRegion?.type || 'none'}
                  label="Type"
                  onChange={e => {
                    const type = e.target.value as any;
                    const defaultSizes: Record<string, number> = { lines: 6, box: 4, table: 8, diagram: 12 };
                    setEditAnswerRegion(prev => ({ ...prev, type, size: defaultSizes[type] || 0 }));
                  }}
                >
                  <MenuItem value="lines">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="LINES" size="small" color="primary" variant="outlined" />
                      Numbered blank lines
                    </Box>
                  </MenuItem>
                  <MenuItem value="box">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="BOX" size="small" color="secondary" variant="outlined" />
                      Single bordered answer box
                    </Box>
                  </MenuItem>
                  <MenuItem value="table">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="TABLE" size="small" color="info" variant="outlined" />
                      Completion grid (trace table etc.)
                    </Box>
                  </MenuItem>
                  <MenuItem value="diagram">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="DIAGRAM" size="small" color="warning" variant="outlined" />
                      Drawing space
                    </Box>
                  </MenuItem>
                  <MenuItem value="none">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="NONE" size="small" variant="outlined" />
                      No answer region
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              {editAnswerRegion?.type && editAnswerRegion.type !== 'none' && (
                <>
                  <TextField
                    label={editAnswerRegion.type === 'lines' ? 'Lines' :
                           editAnswerRegion.type === 'box' ? 'Lines'
                           : editAnswerRegion.type === 'table' ? 'Cells'
                           : 'Size'}
                    type="number" size="small"
                    helperText={editAnswerRegion.type === 'lines' ? 'Number of numbered blank lines'
                               : editAnswerRegion.type === 'box' ? 'Height in text lines'
                               : editAnswerRegion.type === 'table' ? 'Number of cells/entries'
                               : 'Relative drawing area size'}
                    value={editAnswerRegion?.size || 3}
                    onChange={e => setEditAnswerRegion(prev => ({ type: prev!.type, size: Number(e.target.value), scaffold: prev!.scaffold }))}
                    sx={{ width: 120 }}
                  />
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary" display="block">Preview</Typography>
                    <Box sx={{ mt: 0.5, border: '1px dashed', borderColor: 'grey.300', p: 1, borderRadius: 1,
                      maxHeight: 80, overflow: 'hidden', opacity: 0.7 }}>
                      {editAnswerRegion.type === 'lines' && (
                        Array.from({ length: Math.min(editAnswerRegion.size || 3, 8) }).map((_, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                            <Typography variant="caption" color="text.disabled" sx={{ minWidth: 16 }}>{i + 1}.</Typography>
                            <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'grey.400', height: 14 }} />
                          </Box>
                        ))
                      )}
                      {editAnswerRegion.type === 'box' && (
                        <Box sx={{ border: '1px solid', borderColor: 'grey.500', height: 40, minWidth: 60, borderRadius: 0.5 }} />
                      )}
                      {editAnswerRegion.type === 'table' && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2px' }}>
                          {Array.from({ length: Math.min(editAnswerRegion.size || 3, 6) }).map((_, i) => (
                            <Box key={i} sx={{ border: '1px solid', borderColor: 'grey.400', p: 0.3, textAlign: 'center' }}>
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {editAnswerRegion.type === 'diagram' && (
                        <Box sx={{ border: '2px dotted', borderColor: 'grey.400', height: 60,
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" color="text.disabled">Drawing area</Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </>
              )}
              <Button variant="contained" size="small" onClick={handleSave} startIcon={<SaveIcon />}>Save</Button>
            </Stack>
          </Box>
        )}

        {tab === 2 && (
          <Stack spacing={2}>
            <Typography variant="subtitle2" gutterBottom>Memo (model answer)</Typography>
            {BlockEditorComp ? (
              <BlockEditorComp content={editMemo} onChange={setEditMemo} />
            ) : (
              editMemo ? (
                <TextField
                  multiline minRows={4} fullWidth size="small"
                  value={JSON.stringify(editMemo, null, 2)}
                  disabled
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">No memo</Typography>
              )
            )}
            <Button variant="contained" size="small" onClick={handleSave} startIcon={<SaveIcon />}>Save Memo</Button>
          </Stack>
        )}

        {tab === 3 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2">Version History</Typography>
              <Button size="small" onClick={loadVersions} startIcon={<HistoryIcon />}>
                {versionsLoaded ? 'Refresh' : 'Load'}
              </Button>
            </Box>
            {versions.map((v: any) => (
              <Paper key={v.version || v.id} variant="outlined" sx={{ p: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption">
                    v{v.version || v.id} — {new Date(v.created_at).toLocaleString()}
                  </Typography>
                  <Button size="small" onClick={() => handleRestore(v.version || v.id)}>Restore</Button>
                </Box>
                {v.marks != null && <Chip label={`${v.marks}m`} size="small" sx={{ mr: 0.5 }} />}
              </Paper>
            ))}
            {versions.length === 0 && versionsLoaded && (
              <Typography variant="body2" color="text.secondary">No versions</Typography>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ──────────────────────────────────────────────────
// Main Studio layout
// ──────────────────────────────────────────────────

const PapergenStudio: React.FC = () => {
  const { token, user } = useAuth();
  const [selectedWs, setSelectedWs] = useState<number | null>(null);
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [markGrid, setMarkGrid] = useState<MarkGrid | null>(null);
  const [selectedNode, setSelectedNode] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [treeKey, setTreeKey] = useState(0); // force refresh

  const loadTree = useCallback(async () => {
    if (!selectedWs || !token) return;
    try {
      const resp = await getWorkspaceTree(selectedWs, token);
      setTree(resp.data.nodes || []);
      setMarkGrid(resp.data.mark_grid || null);
    } catch (e) { console.error('load tree:', e); }
  }, [selectedWs, token]);

  useEffect(() => { loadTree(); }, [loadTree, treeKey]);

  const handleGround = (result: GroundingResult) => {
    // Sources pane reports what filtered; gap details available here
  };

  const handleAddChild = async (parentId: number | null) => {
    if (!selectedWs || !token) return;
    try {
      await createPaperNode(selectedWs, { heading: 'New Node', parent_id: parentId }, token);
      setTreeKey(k => k + 1);
    } catch (e) { console.error('add node:', e); }
  };

  const handleDeleteNode = async (id: number) => {
    if (!token) return;
    if (!window.confirm('Delete this node and all its children?')) return;
    await deletePaperNode(id, token);
    if (selectedNode === id) setSelectedNode(null);
    setTreeKey(k => k + 1);
  };

  const handleRegenerateNode = async (id: number) => {
    if (!token) return;
    await regenerateNode(id, token);
    setTreeKey(k => k + 1);
  };

  const handleReorder = async (nodeId: number, params: { new_ordinal: number; new_parent_id?: number | null }) => {
    if (!token) return;
    try {
      await reorderPaperNode(nodeId, params, token);
      setTreeKey(k => k + 1);
    } catch (e) {
      console.error('reorder failed:', e);
    }
  };

  const handleGenerate = async () => {
    if (!selectedWs || !token) return;
    setGenerating(true);
    try {
      await generateNodes(selectedWs, { grade: 11, topic: '' }, token);
      setTreeKey(k => k + 1);
    } catch (e) {
      console.error('generate:', e);
      alert('Generation failed. See console.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!token || !user) return;
    const title = prompt('Workspace title:');
    if (!title) return;
    try {
      const axios = (await import('axios')).default;
      const resp = await axios.post(`${API}/workspaces/`, {
        title, subject: 'IT', grade: 11,
      }, { headers: { Authorization: `Token ${token}` } });
      setSelectedWs(resp.data.id);
    } catch (e) { console.error('create ws:', e); }
  };

  const handleExportDocx = async (marking: boolean) => {
    if (!selectedWs || !token) return;
    try {
      const resp = await renderDocx(selectedWs, marking, token);
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = marking ? 'memo.docx' : 'paper.docx';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed: ' + e); }
  };

  const handleExportPdf = async () => {
    if (!selectedWs || !token) return;
    try {
      const resp = await renderLatexPdf(selectedWs, token);
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = 'paper.pdf'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert('PDF export failed: ' + e); }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 2, gap: 2, overflow: 'hidden' }}>
      {/* Toolbar */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ flex: 1 }}>Papergen Studio</Typography>
        <Tooltip title="Download .docx (paper)">
          <Button size="small" variant="outlined" onClick={() => handleExportDocx(false)} startIcon={<DownloadIcon />}>
            Paper .docx
          </Button>
        </Tooltip>
        <Tooltip title="Download .docx (marking copy)">
          <Button size="small" variant="outlined" onClick={() => handleExportDocx(true)} startIcon={<DownloadIcon />}>
            Memo .docx
          </Button>
        </Tooltip>
        <Tooltip title="Download PDF">
          <Button size="small" variant="outlined" onClick={handleExportPdf} startIcon={<PdfIcon />}>
            PDF
          </Button>
        </Tooltip>
      </Stack>

      <WorkspacePicker
        token={token}
        selectedId={selectedWs}
        onSelect={setSelectedWs}
        onCreate={handleCreateWorkspace}
      />

      {!selectedWs ? (
        <Paper variant="outlined" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select or create a workspace to begin
          </Typography>
        </Paper>
      ) : (
        /* Three panes */
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '260px 1fr 360px', gap: 2, overflow: 'hidden' }}>
          <SourcesPane token={token} onGrounded={handleGround} />
          <TreePane
            tree={tree} markGrid={markGrid} selectedId={selectedNode}
            onSelect={setSelectedNode} onDelete={handleDeleteNode}
            onRegenerate={handleRegenerateNode} onAddChild={handleAddChild}
            onGenerate={handleGenerate} generating={generating}
            onReorder={handleReorder}
          />
          <ContextPanel token={token} workspaceId={selectedWs}
            selectedId={selectedNode} onRefresh={() => setTreeKey(k => k + 1)} />
        </Box>
      )}
    </Box>
  );
};

export default PapergenStudio;
